import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {GoldenRecordService} from "../../../Services/goldenRecord.service";
import {LoginService} from "../../../Services/login.service";
import {GoldenRecord} from "../../../Model/goldenRecord";
import {BackupService} from "../../../Services/CatalogManagement/backup.service";
import {Backup} from "../../../Model/System/Backup";
import {DxContextMenuComponent} from "devextreme-angular";
import {TranslateService} from "@ngx-translate/core";
import {SystemService} from "../../../Services/system.service";
import {SignalrService} from "../../../Services/signalr.service";
import {custom} from "devextreme/ui/dialog";
import {lastValueFrom} from "rxjs";
import {GoldenRecordRow} from "./golden-record-row.model";

@Component({
    selector: 'app-golden-record-stage',
    templateUrl: './golden-record-stage.component.html',
    styleUrls: ['./golden-record-stage.component.css'],
    standalone: false
})
export class GoldenRecordStageComponent implements OnInit {

  @ViewChild('rowMenu', {static: false})
  rowMenu?: DxContextMenuComponent;

  public goldenRecord?: GoldenRecordRow;
  public backups: Backup[] = [];
  public selectedCatalogId: string = '';
  public selectedCatalogName: string = '';

  private readonly destroyRef = inject(DestroyRef);
  private menuContext?: { type: 'goldenRecord', row: GoldenRecordRow } | { type: 'backup', backup: Backup };

  public currentMenuItems: any[] = [];

  private _loadVersion = 0;

  private readonly goldenRecordMenuItems = [
    {id: 'delete', text: this.translate.instant('Löschen')},
  ];

  private readonly backupMenuItems = [
    {id: 'promote', text: this.translate.instant('Zum Golden Record promoten')},
    {id: 'delete', text: this.translate.instant('Löschen')},
  ];

  constructor(
    private readonly router: Router,
    private readonly goldenRecordService: GoldenRecordService,
    private readonly backupService: BackupService,
    private readonly loginService: LoginService,
    private readonly translate: TranslateService,
    private readonly systemService: SystemService,
    private readonly signalrService: SignalrService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadGoldenRecord();

    this.signalrService.refreshCatalogList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadGoldenRecord());
  }

  private async loadGoldenRecord() {
    const customerId = this.loginService.currentCustomer.id;
    const version = ++this._loadVersion;

    try {
      const gr = await this.goldenRecordService.getGoldenRecord(customerId);
      if (version !== this._loadVersion) return;

      this.goldenRecord = {
        id: gr.id,
        catalogId: gr.catalogId,
        catalogName: gr.catalogName,
        createdAt: gr.createdAt
      };
      await this.loadBackups(customerId, gr.catalogId, gr.previousCatalogId);
      if (version !== this._loadVersion) return;

      this.syncSelection(gr.catalogId);
    } catch {
      if (version === this._loadVersion) {
        this.goldenRecord = undefined;
        this.backups = [];
        this.selectedCatalogId = '';
        this.selectedCatalogName = '';
      }
    }
  }

  private syncSelection(defaultCatalogId: string): void {
    if (!this.selectedCatalogId) {
      this.selectedCatalogId = defaultCatalogId;
      this.selectedCatalogName = this.goldenRecord?.catalogName ?? '';
      return;
    }

    if (this.selectedCatalogId === this.goldenRecord?.catalogId) {
      this.selectedCatalogName = this.goldenRecord.catalogName;
      return;
    }

    const matchingBackup = this.backups.find(b => b.catalogId === this.selectedCatalogId);
    if (matchingBackup) {
      this.selectedCatalogName = matchingBackup.catalogName ?? '';
    } else {
      // previously selected catalog no longer exists → fall back to golden record
      this.selectedCatalogId = defaultCatalogId;
      this.selectedCatalogName = this.goldenRecord?.catalogName ?? '';
    }
  }

  private async loadBackups(customerId: string, catalogId: string, previousCatalogId?: string) {
    try {
      let backups = await lastValueFrom(this.backupService.getBackupsByCatalog(customerId, catalogId));

      if (previousCatalogId && previousCatalogId !== catalogId) {
        try {
          const previousBackups = await lastValueFrom(this.backupService.getBackupsByCatalog(customerId, previousCatalogId));
          backups = [...backups, ...previousBackups];
        } catch { /* keine backups */ }
      }

      const uniqueBackups = new Map(backups.map(b => [b.id, b]));
      this.backups = Array.from(uniqueBackups.values()).sort((a, b) =>
        new Date(b.backUpTimeStamp!).getTime() - new Date(a.backUpTimeStamp!).getTime()
      );
    } catch {
      this.backups = [];
    }
  }

  onRowClick(catalogId: string, catalogName: string): void {
    this.selectedCatalogId = catalogId;
    this.selectedCatalogName = catalogName;
  }

  openCatalog(catalogId: string): void {
    const customerId = this.loginService.currentCustomer.id;
    this.router.navigate([
      '/dataprocess/golden-record/catalog',
      customerId,
      catalogId,
      'infos'
    ]);
  }

  openGoldenRecordMenu(e: any, row: GoldenRecordRow) {
    this.menuContext = {type: 'goldenRecord', row};
    this.currentMenuItems = this.goldenRecordMenuItems;
    this.showMenu(e);
  }

  openBackupMenu(e: any, backup: Backup) {
    this.menuContext = {type: 'backup', backup};
    this.currentMenuItems = this.backupMenuItems;
    this.showMenu(e);
  }

  private showMenu(e: any) {
    const targetElement = e.element as HTMLElement;
    this.rowMenu?.instance.option('target', targetElement);
    this.rowMenu?.instance.option('dataSource', this.currentMenuItems);
    this.rowMenu?.instance.show();
  }

  async onMenuItemClick(event: any) {
    if (!this.menuContext)
      return;

    const menuItemId = event.itemData.id;

    if (menuItemId === 'delete') {
      if (this.menuContext.type === 'goldenRecord') {
        await this.onDeleteGoldenRecord(this.menuContext.row);
      } else {
        await this.onDeleteBackup(this.menuContext.backup);
      }
    }

    if (menuItemId === 'promote' && this.menuContext.type === 'backup') {
      await this.onPromoteBackup(this.menuContext.backup);
    }
  }

  private async onPromoteBackup(backup: Backup) {
    const confirmDialog = custom({
      title: this.translate.instant('Backup promoten?'),
      messageHtml: `${this.translate.instant('Soll dieses Backup zum Golden Record promoted werden?')}<br/><br/><b>${backup.catalogName}</b><br/><br/>${this.translate.instant('Der aktuelle Golden Record wird automatisch als Backup gesichert.')}`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    const customerId = this.loginService.currentCustomer.id;

    try {
      await this.goldenRecordService.promoteBackup(backup.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Backup wird zum Golden Record promoted. Bitte warten Sie auf die Fertigstellung der Hintergrund-Jobs.'));
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Promoten des Backups'));
    }
  }

  private async onDeleteGoldenRecord(row: GoldenRecordRow) {
    const confirmDialog = custom({
      title: this.translate.instant('Wirklich löschen?'),
      messageHtml: `${this.translate.instant('Wollen Sie diesen Golden Record wirklich löschen?')}<br/><br/><b>${row.catalogName}</b>`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    const customerId = this.loginService.currentCustomer.id;
    try {
      await this.goldenRecordService.delete(row.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Golden Record erfolgreich gelöscht'));
      await this.loadGoldenRecord();
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Löschen des Golden Records'));
    }
  }

  private async onDeleteBackup(backup: Backup) {
    const confirmDialog = custom({
      title: this.translate.instant('Wirklich löschen?'),
      messageHtml: `${this.translate.instant('Wollen Sie dieses Backup wirklich löschen?')}<br/><br/><b>${backup.catalogName}</b>`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    try {
      this.backupService.deleteBackup(backup.id);
      this.systemService.notifyInfo(this.translate.instant('Backup erfolgreich gelöscht'));
      const customerId = this.loginService.currentCustomer.id;
      if (this.goldenRecord) {
        await this.loadBackups(customerId, this.goldenRecord.catalogId);
      }
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Löschen des Backups'));
    }
  }
}
